import React, { useState } from 'react';
import { Button, Card, Heading, Text } from '../ui';
import { DOCUMENT_TYPES, getDocuments } from './shared/DocumentTypeConfig';
import useDocumentSizes from './shared/useDocumentSizes';
import { formatFileSize } from '../../utils/imageValidation';

/**
 * Displays a list of saved documents for a given type.
 * Shows name, date, and allows viewing or deleting.
 *
 * `renderItemActions(doc)` adds a type's own controls to each row (a
 * proposal's online-link controls). Without `onAdd` / `onDelete` (a view-only
 * user) those controls aren't shown.
 */
export default function DocumentList({
  projectData,
  docTypeKey,
  onView,
  onDelete,
  onBack,
  onAdd,
  renderItemActions = null,
}) {
  const [deletingIndex, setDeletingIndex] = useState(null);
  const config = DOCUMENT_TYPES[docTypeKey];
  const documents = getDocuments(projectData, docTypeKey);
  const sizes = useDocumentSizes(documents);
  const docSize = (doc) =>
    sizes[doc.combinedPdfUrl || doc.url || doc.compositionUrl];

  const handleDelete = async (doc, index) => {
    if (!window.confirm(config.deleteConfirm || 'Delete this document?')) {
      return;
    }
    setDeletingIndex(index);
    try {
      await onDelete(doc.createdAt);
    } finally {
      setDeletingIndex(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getDocName = (doc, index) => {
    if (doc.name) return doc.name;
    if (doc.filename) return doc.filename;
    if (doc.roomName) return doc.roomName;
    if (doc.structureName) return `${config.title} - ${doc.structureName}`;
    return `${config.title} #${index + 1}`;
  };

  return (
    <div className="doc-list">
      <div className="doc-list__header">
        <Button variant="ghost" tone="dark" size="sm" onClick={onBack}>
          &larr; Back
        </Button>
        <Heading level={3} tone="dark" style={{ color: config.color, flex: 1 }}>
          {config.title}
        </Heading>
        {onAdd && (
          <Button variant="secondary" tone="dark" size="sm" onClick={onAdd}>
            + New
          </Button>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="doc-list__empty">
          <Text tone="dark" variant="subtle">
            No {config.title.toLowerCase()} documents yet.
          </Text>
          {onAdd && (
            <Button
              variant="secondary"
              tone="dark"
              size="sm"
              onClick={onAdd}
              className="doc-list__create-btn"
            >
              Create your first {config.title}
            </Button>
          )}
        </div>
      ) : (
        <div className="doc-list__items">
          {documents.map((doc, index) => (
            <Card
              key={doc.createdAt || index}
              variant="glass"
              padding="sm"
              className="doc-list__item"
            >
              <div
                className="doc-list__item-content"
                onClick={() => onView(doc)}
              >
                <div
                  className="doc-list__item-indicator"
                  style={{ backgroundColor: config.color }}
                />
                <div className="doc-list__item-info">
                  <Text tone="dark" size="sm">
                    {getDocName(doc, index)}
                  </Text>
                  <Text tone="dark" variant="subtle" size="xs">
                    {formatDate(doc.createdAt)}
                    {typeof docSize(doc) === 'number' &&
                      ` · ${formatFileSize(docSize(doc))}`}
                  </Text>
                </div>
              </div>
              {renderItemActions?.(doc)}
              {onDelete && (
                <button
                  type="button"
                  className="doc-list__item-delete"
                  onClick={() => handleDelete(doc, index)}
                  disabled={deletingIndex === index}
                  title="Delete document"
                >
                  {deletingIndex === index ? '...' : '×'}
                </button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
